import { useDebounce } from '@ipfabric/ui-components/hooks/useDebounce';
import { useEffect, useState } from 'react';
import { useApi } from 'utils/request/useApi';
import { yamlSyntaxError } from './CustomDiscoveryDefinitionRow';
import {
  type DefinitionCheck,
  getDefinitionCheck,
  setDefinitionCheck,
} from './definitionChecks';

export type DefinitionCheckState =
  | DefinitionCheck
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'unavailable' };

type CheckResponse = {
  results: { valid: boolean; name?: string; error?: string }[];
};

const CHECK_DELAY_MS = 500;

/**
 * Checks the definition text with the server once typing pauses; a text
 * checked before is not sent again. `onChecked` runs when a new result
 * arrives.
 */
export const useDefinitionCheck = (
  yaml: string,
  onChecked: () => void,
): DefinitionCheckState => {
  const api = useApi();
  const debounce = useDebounce(CHECK_DELAY_MS, { waitForAsync: true });
  const [state, setState] = useState<DefinitionCheckState>(
    () => getDefinitionCheck(yaml) ?? { status: 'idle' },
  );

  useEffect(() => {
    // An empty text or a YAML syntax error is reported by the form itself.
    if (yaml.trim() === '' || yamlSyntaxError(yaml) !== null) {
      setState({ status: 'idle' });
      return;
    }
    const known = getDefinitionCheck(yaml);
    if (known !== undefined) {
      setState(known);
      return;
    }
    setState({ status: 'checking' });
    let current = true;
    debounce(async () => {
      let next: DefinitionCheckState;
      try {
        const { results } = await api.request<
          CheckResponse,
          { definitions: string[] }
        >({
          url: '/settings/custom-discovery-definitions/validate',
          method: 'POST',
          data: { definitions: [yaml] },
        });
        const [result] = results;
        next = result?.valid
          ? { status: 'valid', name: result.name ?? '' }
          : { status: 'invalid', error: result?.error ?? 'does not load' };
        setDefinitionCheck(yaml, next);
      } catch {
        next = { status: 'unavailable' };
      }
      if (current) {
        setState(next);
        onChecked();
      }
    });
    return () => {
      current = false;
    };
  }, [yaml, onChecked]);

  return state;
};
